import water from '@london-life/game/city/docklands-water.json' with { type: 'json' };
import { BILLBOARDS, BOARD_SIZES } from '@london-life/contracts';

// map real London coordinates onto the game ground: east is +x, north is -z
export const GEO = { lon0: 0.0125, lat0: 51.4975, unitsPerDegree: 3900, latScale: Math.cos((51.4975 * Math.PI) / 180) };
export type { MapPoint } from '@london-life/game/city/types';
import type { MapPoint } from '@london-life/game/city/types';
export const geo = (lon: number, lat: number): MapPoint => [
  +((lon - GEO.lon0) * GEO.latScale * GEO.unitsPerDegree).toFixed(2),
  +(-(lat - GEO.lat0) * GEO.unitsPerDegree).toFixed(2),
];

// the land slab covers the OpenStreetMap bounding box
const [west, south, east, north] = water.source.bbox;
export const LAND = {
  x: (geo(west, 0)[0] + geo(east, 0)[0]) / 2,
  z: (geo(0, south)[1] + geo(0, north)[1]) / 2,
  width: geo(east, 0)[0] - geo(west, 0)[0],
  depth: geo(0, south)[1] - geo(0, north)[1],
  ground: '#a3c986',
  edge: '#d9cfae',
};

// join the clipped Thames pieces into closed rings (the bounding box edge closes them)
const ring = (runs: number[][][]) => runs.flat().map(([lon, lat]) => geo(lon, lat));
const thamesEast = water.thames
  .find((piece) => piece.id === 837647356)!
  .runs[0].slice()
  .reverse()
  .concat(
    water.thames.find((piece) => piece.id === 837647358)!.runs[0],
    water.thames.find((piece) => piece.id === 837647355)!.runs[0],
    water.thames.find((piece) => piece.id === 837647355)!.runs[1],
  );
export const WATER: { name: string; ring: MapPoint[] }[] = [
  { name: 'River Thames', ring: ring([thamesEast]) },
  { name: 'River Thames', ring: ring(water.thames.find((piece) => piece.id === 199827370)!.runs) },
  { name: 'River Thames', ring: ring(water.thames.find((piece) => piece.id === 30917227)!.runs) },
  ...water.docks.map((dock) => ({ name: dock.name, ring: ring([dock.pts]) })),
];

// the real place for a point on the ground (the other way round from `geo`)
export const lonLatOf = ([x, z]: MapPoint) => ({
  lon: +(x / (GEO.latScale * GEO.unitsPerDegree) + GEO.lon0).toFixed(5),
  lat: +(GEO.lat0 - z / GEO.unitsPerDegree).toFixed(5),
});

// The two big airports, joined to the map's edges as Lagos Life's airport is: Heathrow off the west edge and
// Gatwick off the south edge (not where they really are: the map would be fifty miles across). Each is drawn
// from this plan (scene: airports.ts): x east and z south of the site's middle, in map units. London City stays
// small between the docks (`CITY_RUNWAY`).
type Spot = [number, number];
type Area = { x: number; z: number; w: number; d: number };
export type AirportSite = {
  id: 'heathrow' | 'gatwick';
  name: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  // runways along x: their two ends' numbers (west end first), as painted on them
  runways: { from: Spot; to: Spot; width: number; ends: [string, string] }[];
  taxiways: [Spot, Spot][];
  apron: Area;
  // the terminal, and its name on the roof running along its long side
  terminal: Area & { h: number };
  // where the jets park at the gates (nose `turn` from +x) and where their jet bridges meet the terminal
  gates: { at: Spot; turn: number; bridge: Spot }[];
  jetCentre: Area;
  helipad: Spot;
  tower: Spot;
  carParks: Area[];
  forecourt: Area;
  // the road in: from the forecourt to the slab's edge, then on to the city's streets (map points)
  road: Spot[];
  joins: MapPoint[];
  label: Spot;
  // the advert boards 201-204, and which way each faces
  boards: Record<number, [number, number, number]>;
};
const westEdge = LAND.x - LAND.width / 2;
const southEdge = LAND.z + LAND.depth / 2;
export const siteAt = (site: Pick<AirportSite, 'x' | 'z'>, [dx, dz]: readonly number[]): MapPoint => [
  +(site.x + dx!).toFixed(2),
  +(site.z + dz!).toFixed(2),
];
export const HEATHROW: AirportSite = {
  id: 'heathrow',
  name: 'HEATHROW AIRPORT',
  x: westEdge - 86,
  z: LAND.z - 34,
  width: 172,
  depth: 128,
  runways: [
    { from: [-78, -46], to: [52, -46], width: 6, ends: ['09L', '27R'] },
    { from: [-78, 46], to: [52, 46], width: 6, ends: ['09R', '27L'] },
  ],
  taxiways: [
    [
      [-78, -36],
      [50, -36],
    ],
    [
      [-78, 36],
      [50, 36],
    ],
    [
      [-70, -46],
      [-70, 46],
    ],
    [
      [50, -46],
      [50, -36],
    ],
    [
      [50, 36],
      [50, 46],
    ],
    [
      [20, 28],
      [20, 36],
    ],
  ],
  apron: { x: 20, z: 13, w: 66, d: 30 },
  terminal: { x: 20, z: -9, w: 62, d: 10, h: 4 },
  gates: [-3, 9, 21, 33, 45].map((dx) => ({ at: [dx, 6] as Spot, turn: Math.PI / 2, bridge: [dx - 3, -4] as Spot })),
  jetCentre: { x: -38, z: 12, w: 30, d: 24 },
  helipad: [-62, 20],
  tower: [-40, -20],
  carParks: [
    { x: 75, z: -40, w: 18, d: 22 },
    { x: 75, z: 4, w: 18, d: 24 },
  ],
  forecourt: { x: 20, z: -18, w: 62, d: 7 },
  road: [
    [51, -18],
    [86, -18],
  ],
  joins: [geo(-0.034, 51.5094)],
  label: [-20, 56],
  boards: {
    201: [74, -24, 0],
    202: [74, 22, 0],
    203: [32, -30, 0],
    204: [-38, 30, 0],
  },
};
export const GATWICK: AirportSite = {
  id: 'gatwick',
  name: 'GATWICK AIRPORT',
  x: LAND.x - 4,
  z: southEdge + 58,
  width: 172,
  depth: 116,
  runways: [{ from: [-76, 34], to: [76, 34], width: 6, ends: ['08R', '26L'] }],
  taxiways: [
    [
      [-76, 22],
      [76, 22],
    ],
    [
      [-68, 22],
      [-68, 34],
    ],
    [
      [68, 22],
      [68, 34],
    ],
  ],
  apron: { x: -6, z: 4, w: 72, d: 26 },
  terminal: { x: -6, z: -15, w: 62, d: 10, h: 4 },
  gates: [-30, -18, -6, 6, 18].map((dx) => ({
    at: [dx, -3] as Spot,
    turn: Math.PI / 2,
    bridge: [dx - 3, -10] as Spot,
  })),
  jetCentre: { x: 50, z: 4, w: 28, d: 22 },
  helipad: [71, 5],
  tower: [40, -22],
  carParks: [
    { x: -50, z: -36, w: 26, d: 18 },
    { x: 38, z: -42, w: 26, d: 14 },
  ],
  forecourt: { x: -6, z: -25, w: 62, d: 8 },
  road: [
    [-6, -27],
    [-6, -58],
  ],
  joins: [geo(0.0085, 51.4845)],
  label: [-6, 47],
  boards: {},
};
export const AIRPORT_SITES = [HEATHROW, GATWICK] as const;

// King's Cross, joined to the map's north edge the way the airports are (as Lagos Life's Jibowu Motor Park stands
// on its own by the expressway): the yellow-brick front with its two great arches and the clock tower, the two
// train sheds over the platforms, the Western Concourse's white lattice dome, King's Cross Square, the
// gasholders and St Pancras's red-brick tower next door. The tracks run out of the sheds to the slab's north edge
// (the trains leave there); the road in runs south to the city's streets. x east and z south of the middle.
export type StationSite = {
  id: 'kingscross';
  name: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  // the front: its middle, how wide and high; the two arches' middles (each opens on a shed)
  front: { x: number; z: number; w: number; h: number };
  // the train sheds: their middles along x, how wide each is, where they start (the front) and end
  sheds: { x: number[]; w: number; from: number; to: number };
  // tracks per shed, and where they meet to leave the slab
  tracks: number;
  throat: number;
  concourse: { x: number; z: number; r: number };
  square: Area;
  hotel: Area & { h: number };
  gasholders: Spot[];
  pancras: Area & { h: number };
  road: Spot[];
  joins: MapPoint[];
  label: Spot;
  // where the pin stands (the front door, on the square)
  door: Spot;
};
const northEdge = LAND.z - LAND.depth / 2;
export const KINGS_CROSS: StationSite = {
  id: 'kingscross',
  name: "KING'S CROSS",
  x: LAND.x - 136,
  z: northEdge - 50,
  width: 104,
  depth: 96,
  front: { x: 0, z: 22, w: 30, h: 7 },
  sheds: { x: [-7.5, 7.5], w: 14, from: 20, to: -22 },
  tracks: 4,
  throat: -34,
  concourse: { x: -24, z: 22, r: 11 },
  square: { x: 0, z: 31, w: 40, d: 9 },
  hotel: { x: -32, z: 34, w: 10, d: 7, h: 4.5 },
  gasholders: [
    [30, -24],
    [38, -18],
    [30, -12],
  ],
  pancras: { x: 31, z: 12, w: 16, d: 24, h: 6 },
  road: [
    [0, 36],
    [0, 48],
  ],
  joins: [geo(-0.034, 51.5094)],
  label: [-24, 4],
  door: [0, 29],
};
// every runway on the map, as map points (jets take off and land on them): London City's between the docks,
// Heathrow's two and Gatwick's
export const CITY_RUNWAY = { from: geo(0.041, 51.5056), to: geo(0.069, 51.5046) };
export const RUNWAYS: { from: MapPoint; to: MapPoint; big: boolean }[] = [
  { ...CITY_RUNWAY, big: false },
  ...AIRPORT_SITES.flatMap((site) =>
    site.runways.map((runway) => ({ from: siteAt(site, runway.from), to: siteAt(site, runway.to), big: true })),
  ),
];
// everything that is drawn: the city and the airports joined to it (the sun's shadows cover all of it)
export const WORLD = (() => {
  const west = HEATHROW.x - HEATHROW.width / 2,
    east = LAND.x + LAND.width / 2;
  const north = KINGS_CROSS.z - KINGS_CROSS.depth / 2,
    south = GATWICK.z + GATWICK.depth / 2;
  return { x: (west + east) / 2, z: (north + south) / 2, width: east - west, depth: south - north };
})();

// the advert boards: one row along the top (north) edge of the map, facing the camera, in the sizes the
// contracts give them (players book them: contracts adverts.ts). x across the map; w × h is the board, lift how
// high its legs are, turn which way it faces
const boardsZ = LAND.z - LAND.depth / 2 + 6;
const BOARD_GAP = 6;
const TOP = BILLBOARDS.filter((board) => board.where === 'top');
const slotsWidth = TOP.reduce((sum, board) => sum + BOARD_SIZES[board.size].w, 0) + (TOP.length - 1) * BOARD_GAP;
export const BILLBOARD_SLOTS = [
  ...TOP.map((board, i) => {
    const { w, h } = BOARD_SIZES[board.size];
    const before = TOP.slice(0, i).reduce((sum, other) => sum + BOARD_SIZES[other.size].w + BOARD_GAP, 0);
    return {
      number: board.number,
      w,
      h,
      x: LAND.x - slotsWidth / 2 + before + w / 2,
      z: boardsZ,
      lift: 4 + h * 0.2,
      turn: 0,
      where: board.where,
      // London's own advert shown until players book it
      advert: i * 3 + 1,
    };
  }),
  ...BILLBOARDS.filter((board) => board.where === 'airport').map((board, i) => {
    const { w, h } = BOARD_SIZES[board.size];
    const spot = HEATHROW.boards[board.number]!;
    const [x, z] = siteAt(HEATHROW, spot);
    return { number: board.number, w, h, x, z, lift: 1.8, turn: spot[2], where: board.where, advert: i * 5 + 2 };
  }),
];
// the river plots: a plate of square plots on the Thames at Woolwich Reach (contracts RIVER_PLOTS), lying on
// the water; one plot is `PLOT_SIZE` across
export const PLOT_SIZE = 2;
export const RIVER_PLOTS_AT = { lon: 0.062, lat: 51.4962 };
// where the camera looks to see the boards
export const BILLBOARD_FOCUS: MapPoint = [LAND.x, boardsZ + 4];
// and the airport's boards (at Heathrow's landside)
export const AIRPORT_FOCUS: MapPoint = siteAt(HEATHROW, [50, -2]);

// name the real areas on the ground
export const AREA_LABELS: { text: string; lon: number; lat: number; width: number; color?: string }[] = [
  { text: 'CANARY WHARF', lon: -0.019, lat: 51.5035, width: 22 },
  { text: 'ISLE OF DOGS', lon: -0.017, lat: 51.493, width: 20 },
  { text: 'POPLAR', lon: -0.016, lat: 51.511, width: 12 },
  { text: 'GREENWICH', lon: -0.008, lat: 51.479, width: 18 },
  { text: 'GREENWICH PENINSULA', lon: 0.006, lat: 51.4965, width: 24 },
  { text: 'SILVERTOWN', lon: 0.03, lat: 51.5005, width: 16 },
  { text: 'ROYAL DOCKS', lon: 0.045, lat: 51.509, width: 20 },
  { text: 'CHARLTON', lon: 0.035, lat: 51.489, width: 14 },
  { text: 'WOOLWICH', lon: 0.065, lat: 51.489, width: 14 },
  { text: 'DEPTFORD', lon: -0.03, lat: 51.479, width: 14 },
  { text: 'RIVER THAMES', lon: -0.006, lat: 51.4995, width: 22, color: '#d6eef7' },
  { text: 'RIVER THAMES', lon: 0.055, lat: 51.4965, width: 22, color: '#d6eef7' },
  { text: 'RIVER PLOTS · BUY A PLOT', lon: 0.062, lat: 51.4938, width: 26, color: '#d6eef7' },
];

// list the cities the selector will offer
export const CITIES = [
  { id: 'london', name: 'London', icon: '🎡', live: true },
  { id: 'manchester', name: 'Manchester', icon: '🐝', live: false },
  { id: 'birmingham', name: 'Birmingham', icon: '🏭', live: false },
] as const;

// list the quick map shortcuts shown above the city
export const MAP_CHIPS = [
  { id: 'billboards', label: 'Billboards', icon: '📢', place: 'megascreen' },
  { id: 'neighbours', label: 'Neighbours', icon: '🏘️', place: 'home' },
  // players' shops (only for players with a character)
  { id: 'shops', label: 'Shops', icon: '🛍️', place: null },
  { id: 'river', label: 'River plots', icon: '🌊', place: 'plots' },
  { id: 'airport', label: 'Airport boards', icon: '✈️', place: null },
  { id: 'jobs', label: 'Jobs', icon: '💼', place: 'office' },
  { id: 'walk', label: 'Walk Greenwich', icon: '🚶', place: null },
] as const;
